import type { PageFaqStoryblok } from '@/types/component-types-sb';
import type { ISbStoryData } from '@storyblok/react/rsc';
import { useEffect, useMemo, useState } from 'react';

export const useTopicsAndTags = (topics: ISbStoryData<PageFaqStoryblok>[]) => {
  const [allFilteredTopics, setAllFilteredTopics] = useState<Array<ISbStoryData<PageFaqStoryblok>>>(topics);
  const [visibleCount, setVisibleCount] = useState<number>(10);
  const [tags, setTags] = useState<string[]>(() =>
    topics.reduce<string[]>((acc, { content: { FAQTag } }) => {
      // skip empty or duplicate tags
      if (FAQTag && !acc.includes(FAQTag)) {
        acc.push(FAQTag);
      }
      return acc;
    }, [])
  );
  const [selectedTag, setSelectedTag] = useState('');
  const [expandedIndex, setExpandedIndex] = useState(-1);

  useEffect(() => {
    if (!topics || topics.length < 1) {
      return;
    }

    const arr: Array<string> = [];
    for (const topic of topics) {
      const currTag = topic.content.FAQTag;

      if (currTag && !arr.includes(currTag)) {
        arr.push(currTag);
      }
    }

    setTags(arr);
  }, [topics]);

  useEffect(() => {
    setAllFilteredTopics(selectedTag === '' ? topics : topics.filter((topic) => topic.content.FAQTag === selectedTag));
    // Reset visible count when filter changes
    setVisibleCount(10);
  }, [selectedTag, topics]);

  useEffect(() => {
    setExpandedIndex(-1);
  }, [allFilteredTopics]);

  // Only show the first 'visibleCount' topics
  const filteredTopics = useMemo(() => {
    return allFilteredTopics.slice(0, visibleCount);
  }, [allFilteredTopics, visibleCount]);

  const loadMore = () => {
    setVisibleCount((prevCount) => prevCount + 10);
  };

  const hasMore = allFilteredTopics.length > visibleCount;

  return {
    topics,
    filteredTopics,
    allFilteredTopics,
    tags,
    selectedTag,
    setSelectedTag,
    expandedIndex,
    setExpandedIndex,
    loadMore,
    hasMore,
  };
};
